import type {
  ApproachType,
  CrewRole,
  FlightPurpose,
  FlightRule,
  FuelUnit,
  HemsMissionSubtype,
  HemsMissionType,
} from "@newaydata/logbook-schemas/generated/typescript/logbook";

/**
 * The flat board-book view model the page and the sync body use. One field per
 * FR-031 / FR-032 / FR-033 / FR-035 column. Times are UTC `HH:mm`; `date` is the
 * UTC date of block off. A blank optional number is `null`, a blank enum is "".
 */

// Runtime copies of the schema enums (the generated TS has types only).
export const FLIGHT_RULES: readonly FlightRule[] = ["VFR", "IFR"];
export const FLIGHT_PURPOSES: readonly FlightPurpose[] = [
  "COMMERCIAL_PASSENGER",
  "COMMERCIAL_CARGO",
  "HEMS",
  "SAR",
  "TRAINING",
  "CHECK_RIDE",
  "PROFICIENCY",
  "FERRY",
  "POSITIONING",
  "TEST",
  "PRIVATE",
  "AERIAL_WORK",
];
/** Fuel units, in the order the picker shows them. `KG` is first: ARA logs kilograms. */
export const FUEL_UNITS: readonly FuelUnit[] = ["KG", "LITER", "LBS", "US_GAL", "PERCENT"];

/** Display label for each fuel unit. */
export const FUEL_UNIT_LABELS: Readonly<Record<FuelUnit, string>> = {
  KG: "kg",
  LITER: "l",
  LBS: "lbs",
  US_GAL: "US gal",
  PERCENT: "%",
};

export const APPROACH_TYPES: readonly ApproachType[] = [
  "ILS_CAT_I",
  "ILS_CAT_II",
  "ILS_CAT_III",
  "LPV",
  "LNAV",
  "RNP",
  "VOR",
  "NDB",
  "GCA",
  "VISUAL",
  "CIRCLING",
];
export const HEMS_MISSION_TYPES: readonly HemsMissionType[] = [
  "PRIMARY",
  "SECONDARY",
  "INTERHOSPITAL_TRANSFER",
  "SAR",
  "WORK_FLIGHT",
  "TRAINING",
  "POSITIONING",
];
export const HEMS_MISSION_SUBTYPES: readonly HemsMissionSubtype[] = [
  "MEDICAL",
  "TECHNICAL_RESCUE",
  "AVALANCHE",
  "WATER_RESCUE",
  "MOUNTAIN",
  "CARGO",
];
/** Fleetplan's crew roles, in Fleetplan's order. A leg exports them unchanged. */
export const CREW_ROLES: readonly CrewRole[] = [
  "PIC",
  "SIC",
  "TCHEMS",
  "TCHHO",
  "TCNVIS",
  "TRE",
  "TRI",
  "SP",
  "SPIC",
  "PICUS",
  "FI",
  "FE",
  "FIE",
  "FII",
  "IRI",
  "IRE",
  "SE",
  "SFI",
  "SFE",
  "LTC",
  "LCC",
  "SVC",
  "TS",
  "ACM",
  "GC",
  "MEDIC",
  "DOC",
  "CC",
  "OPS",
];

/** Display label for each crew role, from Fleetplan's roles list. */
export const CREW_ROLE_LABELS: Readonly<Record<CrewRole, string>> = {
  PIC: "Pilot in Command",
  SIC: "Second in Command",
  TCHEMS: "Technical Crew Member HEMS",
  TCHHO: "Technical Crew Member HHO",
  TCNVIS: "Technical Crew Member NVIS",
  TRE: "Typerating Examiner",
  TRI: "Typerating Instructor",
  SP: "Student Pilot",
  SPIC: "Student Pilot in Command",
  PICUS: "Pilot in Command under Supervision",
  FI: "Flight Instructor",
  FE: "Flight Examiner",
  FIE: "Flight Instructor Examiner",
  FII: "Flight Instructor Instructor",
  IRI: "Instrument Rating Instructor",
  IRE: "Instrument Rating Examiner",
  SE: "Senior Examiner",
  SFI: "Synthetic Flight Instructor",
  SFE: "Synthetic Flight Examiner",
  LTC: "Line Training Captain",
  LCC: "Line Check Captain",
  SVC: "Supervision Captain",
  TS: "Task Specialist",
  ACM: "Additional Crew Member (SPO.GEN.105)",
  GC: "Ground Crew",
  MEDIC: "Paramedic",
  DOC: "Doctor",
  CC: "Cabin Crew",
  OPS: "Operations Personnel",
};

/** One crew row on a leg (FR-033 core + crew-log-detail + nvis). */
export interface CrewEntry {
  role: CrewRole;
  familyName: string;
  givenNames: string;
  /** Fleetplan user id when picked from the crew pack; "" for free text. */
  externalId: string;
  seat: string;
  blockTime: number | null;
  flightTime: number | null;
  takeoffsDay: number | null;
  takeoffsNight: number | null;
  landingsDay: number | null;
  landingsNight: number | null;
  ifrApproaches: number | null;
  checkFlight: boolean;
  nightTime: number | null;
  ifrTime: number | null;
  hoistDay: number | null;
  hoistNight: number | null;
  hoistStretcher: number | null;
  hoistDoublePax: number | null;
  hoistSinglePax: number | null;
  nvis: boolean;
  nvisCheckFlight: boolean;
}

/** One flight entry (a single leg). */
export interface LogEntry {
  id: string; // sync identity (Leg.external_id); "" until assigned
  updatedAt: number; // epoch ms, per-entry LWW key (Leg.external_updated_at)
  lockedAt: string; // ISO datetime when sealed after export; "" while editable
  date: string; // yyyy-mm-dd, UTC date of block off
  departure: string;
  departureName?: string;
  destination: string;
  destinationName?: string;
  flightNumber: string;
  flightRule: FlightRule;
  flightPurpose: FlightPurpose | "";
  approachType: ApproachType | "";
  blockOff: string; // HH:mm UTC
  takeoff: string; // HH:mm UTC
  landing: string; // HH:mm UTC
  blockOn: string; // HH:mm UTC
  landings: number;
  fuelUplBefore: number | null;
  fuelAtTakeoff: number | null;
  fuelUplAfter: number | null;
  fuelAtLanding: number | null;
  passengers: number | null;
  missionType: HemsMissionType | "";
  missionSubtype: HemsMissionSubtype | "";
  catA: boolean;
  engine1N1: number | null;
  engine1N2: number | null;
  engine1Cdv: number | null;
  engine2N1: number | null;
  engine2N2: number | null;
  engine2Cdv: number | null;
  winchCycles: number | null;
  winchMotorHours: number | null;
  deliveryNote: string;
  internalRef: string;
  remarks: string;
  crew: CrewEntry[];
}

/** Per-book header (FR-031 + carried-forward extension fields). */
export interface BookHeader {
  updatedAt: number; // epoch ms, LWW key of the header sync row
  dateOpened: string;
  dateClosed: string;
  /** Unit of every fuel quantity in this book. A blank unit is not exported to Fleetplan. */
  fuelUnit: FuelUnit | "";
  cfFlightTime: number | null;
  cfBlockTime: number | null;
  cfLandings: number | null;
  cfEngine1N1: number | null;
  cfEngine1N2: number | null;
  cfEngine1Cdv: number | null;
  cfEngine2N1: number | null;
  cfEngine2N2: number | null;
  cfEngine2Cdv: number | null;
  cfWinchHours: number | null;
}

/** The view the page binds: one book, its header, many entries. */
export interface BoardBook {
  registration: string;
  header: BookHeader;
  entries: LogEntry[];
  updatedAt: number; // newest per-entry stamp (display only)
}

/** Seats Fleetplan takes: 1 Left, 2 Right, 3 Cabin. */
export const SEATS = ["Left", "Right", "Cabin"] as const;

// Fleetplan needs a seat for each crew member (Patrick Liess, 2026-10-19). Helicopter
// pilots fly from the right seat. A role without a default gets no seat, and the export
// check then asks the pilot for one.
const ROLE_SEAT: Partial<Record<CrewRole, string>> = {
  PIC: "Right",
  SIC: "Left",
  TCHEMS: "Left",
  DOC: "Cabin",
  MEDIC: "Cabin",
};

export const defaultSeat = (role: CrewRole): string => ROLE_SEAT[role] ?? "";

/** A pilot seat holds one person; the cabin holds many. */
export const seatTaken = (seat: string, others: CrewEntry[]): boolean =>
  seat !== "" && seat !== "Cabin" && others.some((c) => c.seat === seat);

/**
 * The seat after a role change: the new default, unless the pilot chose another seat. A
 * default pilot seat that another crew member already has stays empty.
 */
export function roleChangeSeat(seat: string, from: CrewRole, to: CrewRole, others: CrewEntry[] = []): string {
  if (seat !== "" && seat !== defaultSeat(from)) return seat;
  const next = defaultSeat(to);
  return seatTaken(next, others) ? "" : next;
}

/**
 * A crew row that a person or the timer adds now, with the default seat of its role. A pilot
 * seat that another crew member already has stays empty, so the export check asks for one.
 */
export function newCrew(role: CrewRole, others: CrewEntry[] = []): CrewEntry {
  const seat = defaultSeat(role);
  return { ...emptyCrew(role), seat: seatTaken(seat, others) ? "" : seat };
}

/**
 * The crew of an entry entered before the seat existed, with each blank seat set to the
 * default of its role. A default pilot seat that another crew member has stays empty.
 */
export function fillSeats(crew: CrewEntry[]): CrewEntry[] {
  const filled = [...crew];
  filled.forEach((c, i) => {
    const seat = defaultSeat(c.role);
    if (c.seat === "" && !seatTaken(seat, filled)) filled[i] = { ...c, seat };
  });
  return filled;
}

export function emptyCrew(role: CrewRole = "PIC"): CrewEntry {
  return {
    role,
    familyName: "",
    givenNames: "",
    externalId: "",
    seat: "",
    blockTime: null,
    flightTime: null,
    takeoffsDay: null,
    takeoffsNight: null,
    landingsDay: null,
    landingsNight: null,
    ifrApproaches: null,
    checkFlight: false,
    nightTime: null,
    ifrTime: null,
    hoistDay: null,
    hoistNight: null,
    hoistStretcher: null,
    hoistDoublePax: null,
    hoistSinglePax: null,
    nvis: false,
    nvisCheckFlight: false,
  };
}

export function emptyEntry(): LogEntry {
  return {
    id: "",
    updatedAt: 0,
    lockedAt: "",
    date: "",
    departure: "",
    departureName: "",
    destination: "",
    destinationName: "",
    flightNumber: "",
    flightRule: "VFR",
    flightPurpose: "",
    approachType: "",
    blockOff: "",
    takeoff: "",
    landing: "",
    blockOn: "",
    landings: 0,
    fuelUplBefore: null,
    fuelAtTakeoff: null,
    fuelUplAfter: null,
    fuelAtLanding: null,
    passengers: null,
    // No default: the pilot picks the mission, and the Fleetplan export refuses a leg without one.
    missionType: "",
    missionSubtype: "",
    catA: false,
    engine1N1: null,
    engine1N2: null,
    engine1Cdv: null,
    engine2N1: null,
    engine2N2: null,
    engine2Cdv: null,
    winchCycles: null,
    winchMotorHours: null,
    deliveryNote: "",
    internalRef: "",
    remarks: "",
    crew: [],
  };
}

export function emptyHeader(): BookHeader {
  return {
    updatedAt: 0,
    dateOpened: "",
    dateClosed: "",
    fuelUnit: "",
    cfFlightTime: null,
    cfBlockTime: null,
    cfLandings: null,
    cfEngine1N1: null,
    cfEngine1N2: null,
    cfEngine1Cdv: null,
    cfEngine2N1: null,
    cfEngine2N2: null,
    cfEngine2Cdv: null,
    cfWinchHours: null,
  };
}

export function emptyBoardBook(): BoardBook {
  return { registration: "", header: emptyHeader(), entries: [], updatedAt: 0 };
}
